import { MeetingsModel, VisitFlowTypesModel } from '@w3lcome/types';
import logSentryException from '_/helpers/logSentryException';
import { meetingsApi, visitFlowTypesApi } from '_/services/api';
import { AxiosRequestConfig } from 'axios';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useUser } from './UserProvider';

export interface MeetingGuest {
  email?: string;
  phone?: string;
  idNumber?: string;
  name?: string;
}
export interface IdNumberGuest {
  idNumber: string;
  email?: string;
  phone?: string;
  extraFields?: Record<string, string>;
  linkId?: string;
}

interface MeetingsData {
  meetings: MeetingsModel[];
  getMeetings(params: any, config?: AxiosRequestConfig): Promise<MeetingsModel[] | undefined>;
  loadMoreMeetings(params: any, config?: AxiosRequestConfig): void;
  meetingsState: MeetingsState;
  visitFlowTypes: VisitFlowTypesModel[];
  addCreatedMeeting(data: MeetingsModel): void;
}

export interface MeetingsState {
  total: number;
  limit: number;
  skip: number;
}

const MeetingsContext = createContext<MeetingsData>({} as MeetingsData);

type MeetingsType = {
  children: React.ReactNode;
};

export const MeetingsProvider: React.FC<MeetingsType> = ({ children }) => {
  const [meetings, setMeetings] = useState<MeetingsModel[]>([]);
  const [visitFlowTypes, setVisitFlowTypes] = useState<VisitFlowTypesModel[]>([]);

  const [meetingsState, setMeetingsState] = useState<MeetingsState>({
    total: 0,
    skip: 0,
    limit: 0,
  });

  const companyId = useSelector((state: any) => state.company.id);

  const host = useSelector((state: any) => state.host);

  const { feathersApp } = useUser();

  useEffect(() => {
    if (companyId) getVisitFlowTypes();
  }, [companyId]);

  async function getVisitFlowTypes() {
    try {
      const { data } = await visitFlowTypesApi.getVisitFlowTypes({
        $select: ['id', 'name', 'placeholder', 'sort'],
        isEnabled: true,
        companyId,
        '$sort[sort]': 1,
      });

      setVisitFlowTypes(data);
    } catch (error) {
      logSentryException({
        error,
        file: 'MeetingsProvider.tsx',
        message: 'Error at getVisitFlowTypes function',
      });
    }
  }

  function addCreatedMeeting(data: MeetingsModel) {
    if (
      companyId === data.companyId &&
      (host.id === data.hostId || data.coHosts?.includes(host.email)) &&
      data.meetingType?.type !== 'credentialing'
    ) {
      setMeetings((prevMeetings) => {
        return [...prevMeetings, data].sort(
          (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()
        );
      });

      setMeetingsState({ ...meetingsState, total: meetingsState.total + 1 });
    }
  }

  function updateMeeting(data: MeetingsModel) {
    if (
      companyId === data.companyId &&
      (host.id === data.hostId || data.coHosts?.includes(host.email))
    ) {
      setMeetings((prevMeetings) =>
        prevMeetings.map((meeting) => (meeting.id === data.id ? data : meeting))
      );
    }
  }

  function removeMeeting(data: MeetingsModel) {
    if (
      companyId === data.companyId &&
      (host.id === data.hostId || data.coHosts?.includes(host.email))
    ) {
      setMeetings((prevMeetings) => prevMeetings.filter((meeting) => meeting.id !== data.id));
      setMeetingsState({ ...meetingsState, total: meetingsState.total - 1 });
    }
  }

  useEffect(() => {
    feathersApp?.service('meetings').on('created', addCreatedMeeting);
    feathersApp?.service('meetings').on('patched', updateMeeting);
    feathersApp?.service('meetings').on('removed', removeMeeting);

    return () => {
      feathersApp?.service('meetings').off('created', addCreatedMeeting);
      feathersApp?.service('meetings').off('patched', updateMeeting);
      feathersApp?.service('meetings').off('removed', removeMeeting);
    };
  }, [feathersApp, companyId, host, meetingsState]);

  async function getMeetings(params: any, config?: AxiosRequestConfig) {
    try {
      const { data, total, skip, limit } = await meetingsApi.getMeetings(
        { ...params, companyId },
        config
      );

      setMeetings(data);
      setMeetingsState({ total, skip, limit });

      return data;
    } catch (error) {
      logSentryException({
        error,
        file: 'MeetingsProvider.tsx',
        message: 'Error at getMeetings function',
      });
    }
  }

  async function loadMoreMeetings(params: any, config?: AxiosRequestConfig) {
    try {
      const { data, total, skip, limit } = await meetingsApi.getMeetings(params, config);
      setMeetings([...meetings, ...data]);
      setMeetingsState({ total, skip, limit });
    } catch (error) {
      logSentryException({
        error,
        file: 'MeetingsProvider.tsx',
        message: 'Error at loadMoreMeetings function',
      });
    }
  }

  return (
    <MeetingsContext.Provider
      value={{
        meetings,
        getMeetings,
        meetingsState,
        loadMoreMeetings,
        visitFlowTypes,
        addCreatedMeeting,
      }}
    >
      {children}
    </MeetingsContext.Provider>
  );
};

export function useMeetingsContext() {
  const context = useContext(MeetingsContext);

  if (!context) {
    throw new Error('useMeetings must be used within a MeetingsProvider');
  }

  return context;
}
